import { Application } from "pixi.js";

import { isBroadcastWindow } from "../../../broadcast/windowMode";

import { installVideoUploadRenderGuard } from "./installVideoUploadRenderGuard";

import SceneCompositor from "./SceneCompositor";

import { compositionConfig } from "./compositionConfig";

import { GpuCanvasMirror } from "./GpuCanvasMirror";
import SceneManager from "../../Managers/SceneManager";

import type { ISceneDB } from "../../../interfaces/Scenes";

// biome-ignore lint/style/useImportType: <explanation>

import PlayerFactory from "./PlayerFactory";

import { IStudio } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import {
  DEFAULT_SCENE_TRANSITION,
  type SceneTransition,
  type StingerConfig,
} from "./SceneTransition";

/** Presupuesto para que WebGPU entregue un adapter antes de arrancar Pixi en WebGL2. */
const WEBGPU_ADAPTER_BUDGET_MS = 3000;

type RendererPreference = "webgpu" | "webgl";

/**
 * Elige el backend de Pixi. `navigator.gpu.requestAdapter()` a veces tarda 30–60 s en la ventana OSR
 * (visto en esta PC con el GPU process sano: la ventana de control lo resuelve en 10 ms) y Pixi lo
 * esperaría indefinidamente → el broadcast quedaba en "Iniciando…" sin explicación. Con el adapter
 * fuera de presupuesto se arranca en WebGL2 (el mismo fallback que Pixi usa cuando WebGPU falla) y
 * queda la marca `eriol-start:gpu-fallback` en la línea de tiempo del arranque (log de soporte).
 */
async function pickRendererPreference(): Promise<RendererPreference> {
  const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;

  if (!gpu) return "webgl";

  let timer: ReturnType<typeof setTimeout> | undefined;

  const budget = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), WEBGPU_ADAPTER_BUDGET_MS);
  });

  try {
    const adapter = await Promise.race([gpu.requestAdapter(), budget]);

    if (adapter === "timeout") {
      performance.mark("eriol-start:gpu-fallback");
      console.warn(
        `[PixiFactory] WebGPU requestAdapter() sin respuesta en ${WEBGPU_ADAPTER_BUDGET_MS} ms → WebGL2`,
      );
      return "webgl";
    }

    return adapter ? "webgpu" : "webgl";
  } catch {
    return "webgl";
  } finally {
    clearTimeout(timer);
  }
}

class PixiFactory {
  private App!: Application;

  private Compositor!: SceneCompositor;

  private Scene: ISceneDB;

  private Players!: PlayerFactory;

  private Canvas!: HTMLCanvasElement;

  private Stream!: MediaStream;

  private OffscreenCanvas!: OffscreenCanvas;

  private Preview?: GpuCanvasMirror | null;

  private OnLive: boolean = false;

  // Resolución de la composición (editor 1920×1080; broadcast OSR la pisa con 4K vía

  // configureComposition() antes del boot).

  private Options = {
    width: compositionConfig.width,

    height: compositionConfig.height,
  };

  private State = {
    isDrawing: false,

    startTime: 0,

    lastScreenshotTime: 0,

    lastDrawTime: 0,
  };

  // AudioContext + destino del mix del broadcast (GlobalAudio), para el audio del

  // stinger. Se pasan al SceneCompositor → StingerPlayer.

  private AudioCtx?: AudioContext;

  private AudioDest?: AudioNode;

  constructor(
    scene: ISceneDB,

    players: PlayerFactory,

    audioCtx?: AudioContext,

    audioDest?: AudioNode,
  ) {
    this.Scene = scene;

    this.Players = players;

    this.AudioCtx = audioCtx;

    this.AudioDest = audioDest;
  }

  private waitForCanvasReady() {
    const searchCanvas = (resolve: (value: unknown) => void) => {
      const canvas = document.querySelector(
        "#canvas-studio",
      ) as HTMLCanvasElement;

      if (!canvas) {
        setTimeout(searchCanvas, 50);

        return;
      }

      resolve(true);
    };

    return new Promise((resolve) => {
      searchCanvas(resolve);
    });
  }

  updateOnLive(onLive: boolean) {
    this.OnLive = onLive;

    this.Compositor.updateOnLive(onLive);
  }

  /**
   * Transición del próximo pase: la de ENTRADA de la escena destino (override
   * por-escena, modelo OBS) o el default si no la configuró. La config vive en
   * model.transition de cada escena.
   */

  private resolveTransition(target: ISceneDB): SceneTransition {
    return target.model.transition ?? DEFAULT_SCENE_TRANSITION;
  }

  /** Pre-construye una escena fuera de pantalla para que el próximo pase sea instantáneo. */

  async prewarmScene(scene: ISceneDB) {
    if (!this.Compositor) return;

    await this.Compositor.prewarm(scene);
  }

  /** Miniatura de una escena fuera de pantalla (ver SceneCompositor.snapshot). */
  async snapshotScene(scene: ISceneDB, width: number, height: number) {
    if (!this.Compositor) return null;

    return this.Compositor.snapshot(scene, width, height);
  }

  async createApp() {
    await this.waitForCanvasReady();
    performance.mark("eriol-start:canvas");

    this.Canvas = document.querySelector("#canvas-studio") as HTMLCanvasElement;

    // captureStream MUST be called BEFORE transferControlToOffscreen —

    // after transfer the canvas is in "placeholder" mode and captureStream throws.

    // 60fps to match the Pixi ticker (browser vsync) — was 30fps which

    // halved the broadcast frame rate vs the editor preview. Higher CPU

    // cost on the encoding path but smoother stream output.

    // En broadcast OSR NO se captura por aquí: NVENC encodea la shared texture del OSR

    // (reemplaza este path). En editor sí, para el preview/MediaStream.

    if (compositionConfig.captureStream) {
      this.Stream = this.Canvas.captureStream(60);
    }

    this.OffscreenCanvas = this.Canvas.transferControlToOffscreen();

    const preference = await pickRendererPreference();

    this.App = new Application();

    await this.App.init({
      canvas: this.OffscreenCanvas,

      width: this.Options.width,

      height: this.Options.height,

      clearBeforeRender: true,

      sharedTicker: true,

      // OSR (captureStream:false) es headless → NVENC lee la shared texture, no hay readback ni canvas

      // visible. useBackBuffer (swap) y preserveDrawingBuffer (copia a CPU) son trabajo muerto ahí; y

      // antialias (MSAA) cuesta GPU que compite con el juego — imperceptible tras el encode H.264. El

      // EDITOR (captureStream:true) los mantiene para el preview del operador. Reduce el costo de GPU

      // del OSR sin tocar fps/resolución de la emisión.

      useBackBuffer: compositionConfig.captureStream,

      backgroundAlpha: 1,

      preserveDrawingBuffer: compositionConfig.captureStream,

      powerPreference: "high-performance",

      antialias: compositionConfig.captureStream,

      autoDensity: false,

      bezierSmoothness: 0,

      eventMode: "none",

      roundPixels: false,

      resolution: 1,

      eventFeatures: {
        move: false, // Pointer/mouse/touch movement

        click: false, // Click/tap events

        wheel: false, // Mouse wheel/scroll events
      },

      preferWebGLVersion: 2,

      // WebGPU como renderer (Chromium 148 de Electron 42 lo trae default-on sobre D3D12). Pixi cae solo

      // a WebGL2 (preferWebGLVersion:2) si la init de WebGPU falla → no hace falta gate manual. Gana en

      // escenas pesadas de filtros/máscaras (menos costo de submission en el main thread del renderer);

      // el handoff a la captura sigue siendo el frame compuesto del compositor (OSR shared-texture intacto).
      // Salvo que requestAdapter() no responda a tiempo: ver pickRendererPreference.

      preference,

      // Unified Garbage Collection (PixiJS 8.15.0+)

      // Aggressive GC for scene-switching app — avoid VRAM accumulation

      gcActive: true,

      gcMaxUnusedTime: 10000, // Release resources after 10s of non-use (was 60s)

      gcFrequency: 5000, // Check every 5s for unused resources (was 30s)
    });

    // Cinturón de seguridad: subir un <video> sin frame decodificado a textura
    // GPU hace que WebGPU tire OperationError en el chokepoint de upload
    // (GpuTextureSystem.onSourceUpdate), llamado desde el render, desde
    // requestVideoFrameCallback y desde los handlers DOM del <video>. Sin esto el
    // preview se congela / el browser source no arranca y Sentry se satura
    // (DESKTOP-A/9, "no anda el browser source"). Debe ir ANTES de crear texturas
    // (el guard shadowea onSourceUpdate y los sources registran su listener en el
    // init). Cubre editor y OSR de emisión (mismo path).
    performance.mark("eriol-start:gpu-init");
    installVideoUploadRenderGuard(this.App);

    this.App.stage.sortableChildren = true;

    this.Compositor = new SceneCompositor(
      this.Scene,

      this.App,

      this.Players,

      undefined,

      this.AudioCtx,

      this.AudioDest,
    );

    this.State.isDrawing = true;

    // Window focus and visibility must never interrupt the video composition.
    if (!isBroadcastWindow()) {
      this.App.ticker.maxFPS = 60;
      this.Preview = GpuCanvasMirror.create(this.App.renderer);
      SceneManager.setPreview(this.Preview);
    }
  }

  /** Precarga el asset de un stinger (llamado al setear la config desde la UI). */

  async prewarmStinger(cfg: StingerConfig) {
    await this.Compositor?.prewarmStinger(cfg);
  }

  private haveAreas() {
    // Espeja el guard de RenderFactory.haveAreas: `areas` puede ser `undefined`

    // en una escena sin áreas, y `deleteArea` (único caller) llamaba acá tras

    // borrar la última → `undefined.length` crasheaba.

    return (this.Scene.model.areas?.length ?? 0) > 0;
  }

  private haveBackground() {
    return this.Scene.model.theme?.background !== undefined;
  }

  private haveWidgets() {
    const widgets = this.Scene.model.widgets;

    return widgets && widgets.length > 0;
  }

  private haveElements() {
    const elements = this.Scene.model.elements;

    return elements && elements.length > 0;
  }

  private async drawFrame() {
    const active = this.Compositor.active;

    // renderSelf corre la secuencia completa (test-pattern / background / areas /

    // overlays / panels / widgets / elements / alerts / mask) sobre el Root de la

    // escena activa, reusando su DrawCache por-área.

    await active.renderSelf(this.OnLive);

    if (!this.OnLive) {
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 200);
      });

      await active.screenshot();
    }
  }

  getStream() {
    return this.Stream;
  }

  async switchScene(scene: ISceneDB, isStreaming: boolean) {
    this.OnLive = isStreaming;

    this.Scene = scene;

    // El compositor construye el destino offscreen, swapea visibilidad (o

    // transiciona) y pausa la escena saliente. Pase O(1) si la escena estaba viva.

    await this.Compositor.switchScene(
      scene,

      isStreaming,

      this.resolveTransition(scene),
    );

    // En no-live captura el thumbnail de la escena ya activa (cache hit barato).

    if (!isStreaming) {
      await this.start();
    } else {
      this.State.isDrawing = true;
    }
  }

  resumeScene() {
    this.State.isDrawing = true;

    this.State.startTime = performance.now();

    this.Compositor.resume();
  }

  async updateScene(scene: ISceneDB) {
    this.Scene = scene;

    await this.Compositor.updateScene(scene);

    this.State.isDrawing = true;
  }

  async updateModel(model: IStudio) {
    this.Scene.model = model;

    if (!this.Compositor) return;

    await this.Compositor.updateModel(model);

    await this.start();
  }

  updatePlayers(players: PlayerFactory) {
    this.Players = players;

    this.Compositor?.updatePlayers(players);
  }

  async start() {
    this.State.isDrawing = true;

    this.State.startTime = performance.now();

    await this.drawFrame();
  }

  stop() {
    this.OnLive = false;

    this.State.isDrawing = false;

    this.Compositor.stop();
  }

  destroy() {
    this.OnLive = false;

    this.stop();

    if (this.Preview !== undefined) {
      SceneManager.clearPreview(this.Preview);
      this.Preview?.destroy();
    }

    // Destruir el compositor ANTES que la App: cada layer remueve sus tickers

    // (mask/alerts) del App.ticker, que debe seguir vivo. Si se destruye la App

    // primero, App.ticker queda null y clear() tira al hacer ticker.remove.

    this.Compositor.destroy();

    try {
      this.App.destroy();
    } catch {
      // PixiJS v8 can throw if Application wasn't fully initialized
    }

    if (this.Stream) {
      const tracks = this.Stream.getTracks();

      if (tracks) {
        tracks.forEach((track) => track.stop());
      }
    }
  }

  sceneDestroy() {
    this.Compositor.clear();
  }

  deleteArea(id: string) {
    this.Compositor.deleteArea(id);

    if (this.Scene?.model?.areas) {
      this.Scene.model = {
        ...this.Scene.model,

        areas: this.Scene.model.areas.filter((area) => area.id !== id),
      };
    }

    if (
      !this.haveAreas() &&
      !this.haveBackground() &&
      !this.haveWidgets() &&
      !this.haveElements()
    ) {
      this.Compositor.active.renderTestPattern();
    }
  }
}

export default PixiFactory;
